import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../services/FishingDB';
import { CatchLog, FishingArea, FishingSession } from '../entities/FishingEntities';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import * as SpeciesService from '../services/SpeciesService';
import type { SpeciesWithTranslation } from '../services/SpeciesService';
import MapView from '../components/MapView';
import { CatchWeatherTrends } from '../components/CatchWeatherTrends';
import {
  convertWeight,
  convertLength,
  convertDepth,
  convertTemperature,
  convertWindSpeed,
  convertPressure,
  convertPrecipitation,
  getWeightUnit,
  getLengthUnit,
  getDepthUnit,
  getTemperatureUnit,
  getWindSpeedUnit,
  getPressureUnit,
  getPrecipitationUnit
} from '../utils/unitConversion';
import '../App.css';

export const CatchDetailPage: React.FC = () => {
  const { catchId } = useParams<{ catchId: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { unitSystem } = useSettings();

  const [catchLog, setCatchLog] = useState<CatchLog | null>(null);
  const [area, setArea] = useState<FishingArea | null>(null);
  const [session, setSession] = useState<FishingSession | null>(null);
  const [speciesList, setSpeciesList] = useState<SpeciesWithTranslation[]>([]);
  const [topCatches, setTopCatches] = useState<CatchLog[]>([]);
  const [loading, setLoading] = useState(true);

  const loadSpecies = useCallback(async () => {
    try {
      const allSpecies = await SpeciesService.getAllSpecies((key: string) => t[key as keyof typeof t] as string, language);
      setSpeciesList(allSpecies);
    } catch (error) {
      console.error('Error loading species:', error);
    }
  }, [t, language]);

  const loadData = useCallback(async () => {
    if (!catchId) {
      navigate('/catches');
      return;
    }

    try {
      const catchData = await db.catches.get(catchId);
      if (!catchData) {
        navigate('/catches');
        return;
      }

      setCatchLog(catchData);

      // Load area if exists
      if (catchData.fishingAreaId) {
        const areaData = await db.areas.get(catchData.fishingAreaId);
        setArea(areaData || null);
      }

      // Load session if exists
      if (catchData.sessionId) {
        const sessionData = await db.sessions.get(catchData.sessionId);
        setSession(sessionData || null);
      }

      // Load top catches from same area AND same species
      if (catchData.fishingAreaId && catchData.species) {
        const allCatches = await db.catches
          .where('fishingAreaId')
          .equals(catchData.fishingAreaId)
          .toArray();
        
        // Filter by same species, sort by weight (descending) and take top 5
        const sorted = allCatches
          .filter(c => c.species === catchData.species && c.weight && c.weight > 0)
          .sort((a, b) => (b.weight || 0) - (a.weight || 0))
          .slice(0, 5);
        
        setTopCatches(sorted);
      }
    } catch (error) {
      console.error('Failed to load catch:', error);
      navigate('/catches');
    } finally {
      setLoading(false);
    }
  }, [catchId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSpecies();
  }, [loadSpecies]);

  const getSpeciesName = (speciesKey: string): string => {
    const species = speciesList.find(s => s.key === speciesKey);
    return species ? species.name : speciesKey;
  };

  const getSpeciesIcon = (speciesKey: string): string => {
    const species = speciesList.find(s => s.key === speciesKey);
    return species ? species.icon : '🐟';
  };

  const getLocalizedWindDirection = (degrees: number): string => {
    const directions = [
      t.north || 'N',
      t.northeast || 'NE', 
      t.east || 'E',
      t.southeast || 'SE',
      t.south || 'S',
      t.southwest || 'SW',
      t.west || 'W',
      t.northwest || 'NW'
    ];
    const index = Math.round(((degrees % 360) / 45)) % 8;
    const arrows = ['↓', '↙', '←', '↖', '↑', '↗', '→', '↘'];
    return `${directions[index]} ${arrows[index]}`;
  };

  const formatWeight = (grams: number | undefined): string => {
    if (!grams) return t.notRecorded || 'Not recorded';
    const converted = convertWeight(grams, unitSystem);
    const unit = getWeightUnit(unitSystem);
    return `${converted.toFixed(2)} ${unit}`;
  };

  const formatLength = (mm: number | undefined): string => {
    if (!mm) return t.notRecorded || 'Not recorded';
    const converted = convertLength(mm, unitSystem);
    const unit = getLengthUnit(unitSystem);
    return `${converted.toFixed(1)} ${unit}`;
  };

  const formatDepth = (mm: number | undefined): string => {
    if (!mm) return t.notRecorded || 'Not recorded';
    const converted = convertDepth(mm, unitSystem);
    const unit = getDepthUnit(unitSystem);
    return `${converted.toFixed(1)} ${unit}`;
  };

  const formatTemperature = (celsius: number | undefined): string => {
    if (celsius === undefined) return t.notRecorded || 'Not recorded';
    const converted = convertTemperature(celsius, unitSystem);
    const unit = getTemperatureUnit(unitSystem);
    return `${converted.toFixed(1)}${unit}`;
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-white text-xl">{t.loading}...</div>
      </div>
    );
  }

  if (!catchLog) {
    return null;
  }

  return (
    <>
      {/* Back Button */}
      <button
        onClick={() => navigate('/catches')}
        className="mb-6 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors flex items-center gap-2"
      >
        ← {t.backToCatches || 'Back to catches'}
      </button>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-4">
              
              {/* Premium Header/Profile Card with Photo */}
              <div className="relative rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6 overflow-hidden">
                {/* Remove individual glow - using page-wide glow now */}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Photo Section */}
                  {catchLog.photo && (
                    <div className="md:col-span-1">
                      <img 
                        src={catchLog.photo} 
                        alt={getSpeciesName(catchLog.species)}
                        className="w-full h-48 md:h-full object-cover rounded-xl shadow-2xl ring-2 ring-white/20"
                      />
                    </div>
                  )}
                  
                  {/* Info Section */}
                  <div className={catchLog.photo ? "md:col-span-2" : "md:col-span-3"}>
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-6xl">{getSpeciesIcon(catchLog.species)}</span>
                      <div className="flex-1">
                        <h1 className="text-4xl font-bold text-white mb-2">
                          {getSpeciesName(catchLog.species)}
                        </h1>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="px-3 py-1 rounded-full text-sm font-medium bg-white/10 text-white">
                            {catchLog.caught ? t.landed : t.released}
                          </span>
                          <span className="text-white/60 text-sm">
                            {formatDate(catchLog.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Key Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                      <div className="rounded-2xl bg-white/[0.03] p-3 border border-white/[0.08]">
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.weightLabel}</div>
                        <div className="text-xl font-semibold text-white">{formatWeight(catchLog.weight)}</div>
                      </div>
                      <div className="rounded-2xl bg-white/[0.03] p-3 border border-white/[0.08]">
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.lengthLabel}</div>
                        <div className="text-xl font-semibold text-white">{formatLength(catchLog.length)}</div>
                      </div>
                      <div className="rounded-2xl bg-white/[0.03] p-3 border border-white/[0.08]">
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.waterTempLabel}</div>
                        <div className="text-xl font-semibold text-white">{formatTemperature(catchLog.waterTemp)}</div>
                      </div>
                      <div className="rounded-2xl bg-white/[0.03] p-3 border border-white/[0.08]">
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.depthLabel}</div>
                        <div className="text-xl font-semibold text-white">{formatDepth(catchLog.fishingDepth)}</div>
                      </div>
                    </div>

                    {/* Notes inside header if they exist */}
                    {catchLog.notes && (
                      <div className="mt-6 pt-6 border-t border-white/10">
                        <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400 mb-2">{t.notesLabel || 'Notes'}</h3>
                        <p className="text-white/80 whitespace-pre-wrap leading-relaxed">{catchLog.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Weather Panel - Matching WeatherApp Style */}
              {(catchLog.airTemp !== undefined || catchLog.windSpeed !== undefined || catchLog.pressure !== undefined) && (
                <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6">
                  <div className="h-full flex flex-col">
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-white/10">
                          {t.weather || 'Väder vid fångst'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">{formatDate(catchLog.timestamp)}</p>
                    </div>

                    <div className="flex-1 flex flex-col justify-center">
                      {/* Large centered temperature display */}
                      {catchLog.airTemp !== undefined && (
                        <div className="text-center mb-8">
                          <div className="text-6xl mb-4">
                            {catchLog.cloudiness && catchLog.cloudiness > 70 ? '☁️' : 
                             catchLog.cloudiness && catchLog.cloudiness > 30 ? '⛅' : '☀️'}
                          </div>
                          <div className="text-4xl font-bold text-white mb-2">
                            {formatTemperature(catchLog.airTemp)}
                          </div>
                        </div>
                      )}

                      {/* Grid of weather details */}
                      <div className="grid grid-cols-2 gap-4">
                        {catchLog.humidity !== undefined && (
                          <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                            <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.humidity || 'Luftfuktighet'}</div>
                            <div className="text-xl font-semibold text-white">{catchLog.humidity}%</div>
                          </div>
                        )}
                        {catchLog.windSpeed !== undefined && (
                          <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                            <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.wind || 'Vind'}</div>
                            <div className="text-xl font-semibold text-white">
                              {catchLog.windDirection !== undefined && (
                                <span className="mr-1">
                                  {getLocalizedWindDirection(catchLog.windDirection)}
                                </span>
                              )}
                              {convertWindSpeed(catchLog.windSpeed, unitSystem).toFixed(1)}
                              {catchLog.windGust && catchLog.windGust > catchLog.windSpeed && (
                                <span>({convertWindSpeed(catchLog.windGust, unitSystem).toFixed(1)})</span>
                              )}
                              {' '}<span className="text-base">{getWindSpeedUnit(unitSystem)}</span>
                            </div>
                          </div>
                        )}
                        {catchLog.pressure !== undefined && (
                          <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                            <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.pressure || 'Lufttryck'}</div>
                            <div className="text-xl font-semibold text-white">{convertPressure(catchLog.pressure, unitSystem).toFixed(0)} {getPressureUnit(unitSystem)}</div>
                          </div>
                        )}
                        {catchLog.precipitation !== undefined && (
                          <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                            <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.precipitation || 'Nederbörd'}</div>
                            <div className="text-xl font-semibold text-white">{convertPrecipitation(catchLog.precipitation, unitSystem).toFixed(1)} {getPrecipitationUnit(unitSystem)}</div>
                          </div>
                        )}
                      </div>

                      {/* Sol & Måne section */}
                      {(catchLog.sunrise || catchLog.sunset || catchLog.moonPhase !== undefined) && (
                        <div className="mt-6 pt-6 border-t border-white/[0.08]">
                          <h3 className="text-sm font-medium text-slate-400 mb-3 uppercase tracking-wider">{t.sunAndMoon}</h3>
                          <div className="grid grid-cols-2 gap-4">
                            {catchLog.sunrise && (
                              <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                                <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">🌅 {t.sunriseLabel}</div>
                                <div className="text-xl font-semibold text-white">
                                  {new Date(catchLog.sunrise).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            )}
                            {catchLog.sunset && (
                              <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                                <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">🌇 {t.sunsetLabel}</div>
                                <div className="text-xl font-semibold text-white">
                                  {new Date(catchLog.sunset).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            )}
                            {catchLog.moonPhase !== undefined && (
                              <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08] col-span-2">
                                <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">🌙 {t.moonPhase}</div>
                                <div className="text-xl font-semibold text-white">
                                  {catchLog.moonPhase === 0 ? '🌑 ' + (t.newMoon || 'Nymåne') :
                                   catchLog.moonPhase < 0.25 ? '🌒 ' + (t.waxingCrescent || 'Tilltagande skära') :
                                   catchLog.moonPhase === 0.25 ? '🌓 ' + (t.firstQuarter || 'Första kvarteret') :
                                   catchLog.moonPhase < 0.5 ? '🌔 ' + (t.waxingGibbous || 'Tilltagande måne') :
                                   catchLog.moonPhase === 0.5 ? '🌕 ' + (t.fullMoon || 'Fullmåne') :
                                   catchLog.moonPhase < 0.75 ? '🌖 ' + (t.waningGibbous || 'Avtagande måne') :
                                   catchLog.moonPhase === 0.75 ? '🌗 ' + (t.lastQuarter || 'Sista kvarteret') :
                                   '🌘 ' + (t.waningCrescent || 'Avtagande skära')}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Weather Trends Widget - Shows daily history */}
              {catchLog.weatherHistory && catchLog.weatherHistory.daily && catchLog.weatherHistory.daily.length > 0 && (
                <CatchWeatherTrends 
                  weatherHistory={catchLog.weatherHistory}
                  catchTimestamp={catchLog.timestamp}
                />
              )}

              {/* Top Catches from Same Species & Area Widget */}
              {topCatches.length > 0 && (
                <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-white mb-4">
                    🏆 {t.topCatches} {getSpeciesName(catchLog.species)} {area && <span className="text-slate-400">• {area.name}</span>}
                  </h2>
                  <div className="space-y-2">
                    {topCatches.map((c, index) => (
                      <div 
                        key={c.id}
                        onClick={() => navigate(`/catches/${c.id}`)}
                        className={`relative flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all ${
                          c.id === catchId 
                            ? 'bg-cyan-500/10 ring-1 ring-cyan-500/30' 
                            : 'bg-white/[0.03] hover:bg-white/[0.05]'
                        }`}
                      >
                        {/* Rank badge */}
                        <div className={`absolute -left-2 -top-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-yellow-950' :
                          index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-950' :
                          index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-orange-950' :
                          'bg-slate-600 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        
                        <div className="flex items-center gap-3 flex-1 ml-4">
                          <span className="text-2xl">{getSpeciesIcon(c.species)}</span>
                          <div className="flex-1">
                            <p className="text-white font-medium">{getSpeciesName(c.species)}</p>
                            <p className="text-slate-400 text-xs">{new Date(c.timestamp).toLocaleDateString(language, { month: 'short', day: 'numeric' })}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-semibold">{formatWeight(c.weight)}</p>
                          {c.length && <p className="text-slate-400 text-sm">{formatLength(c.length)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Side Info */}
            <div className="space-y-4">
              
              {/* Map Widget - TOP RIGHT like WeatherApp */}
              {(catchLog.lat && catchLog.lng) && (
                <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-white mb-4">{t.locationLabel || 'Location'}</h2>
                  {area && (
                    <button
                      onClick={() => navigate(`/areas`)}
                      className="w-full text-left mb-3 p-3 bg-white/[0.03] rounded-xl hover:bg-white/[0.05] transition-colors border border-white/[0.08]"
                    >
                      <p className="text-white font-medium flex items-center justify-between">
                        <span>{area.name}</span>
                        <span className="text-slate-400">→</span>
                      </p>
                    </button>
                  )}
                  <div className="rounded-xl overflow-hidden border border-white/[0.08]" style={{ height: '200px' }}>
                    <MapView
                      initialCenter={{ lat: catchLog.lat, lng: catchLog.lng }}
                      initialZoom={13}
                      selectedCoordinates={{ lat: catchLog.lat, lng: catchLog.lng }}
                      onMapClick={() => {}}
                    />
                  </div>
                  <p className="text-slate-400 text-xs mt-3 text-center font-mono">
                    {catchLog.lat.toFixed(4)}, {catchLog.lng.toFixed(4)}
                  </p>
                </div>
              )}
              
              {/* Session Widget with Link */}
              {session && (
                <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-white mb-4">{t.fishingSession || 'Fishing Session'}</h2>
                  <button
                    onClick={() => navigate(`/sessions`)}
                    className="w-full text-left p-4 bg-white/[0.03] rounded-xl hover:bg-white/[0.05] transition-colors border border-white/[0.08]"
                  >
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-slate-400 text-xs mb-1">{t.start || 'Start'}</p>
                          <p className="text-white font-semibold">{new Date(session.startTime).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        {session.endTime && (
                          <div>
                            <p className="text-slate-400 text-xs mb-1">{t.end || 'End'}</p>
                            <p className="text-white font-semibold">{new Date(session.endTime).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-white/[0.08]">
                        <p className="text-slate-400 text-sm">→ {t.sessions || 'View Session'}</p>
                        {session.catchCount && (
                          <span className="text-slate-400 text-xs">{session.catchCount} {t.catches || 'catches'}</span>
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Fishing Score Widget */}
              {catchLog.fishingScore !== undefined && (
                <div className="relative rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6 overflow-hidden">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 via-orange-500/5 to-transparent pointer-events-none"></div>
                  
                  <div className="relative">
                    <h2 className="text-lg font-semibold text-white mb-4">{t.fishingScore || 'Fishing Score'}</h2>
                    <div>
                      <div className="text-5xl font-bold mb-3 text-white">
                        {catchLog.fishingScore}
                        <span className="text-2xl text-slate-400"> / 100</span>
                      </div>
                      <div className="relative w-full h-3 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
                          style={{ width: `${catchLog.fishingScore}%` }}
                        ></div>
                      </div>
                      <p className="text-slate-400 text-sm mt-2 text-center">
                        {catchLog.fishingScore >= 80 ? t.excellent :
                         catchLog.fishingScore >= 60 ? t.good :
                         catchLog.fishingScore >= 40 ? t.fair : t.keepTrying}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
    </>
  );
};
